<template>
  <div class="row">
    <!-- TKP-91 / tasarım s.22 md.1–6 — tek ekranlı sihirbaz: solda adımlar, sağda CANLI önizleme.
         Seçim yapıldıkça metin ve içindeki tablolar aynı ekranda güncellenir; "Metni Oluştur" ve
         "Oluşan Taslağı Kullan" iki adımı kalktı, son adımda metin üretilip yayımlanır. -->
    <div class="col-lg-5">
      <div class="card">
        <div class="card-body">
          <!-- 1. adım: metin tipi -->
          <h5>{{ $t("legal-document-wizard.step.type") }}</h5>
          <select class="form-control mb-3" v-model="state.type" data-test="type" @change="typeChanged">
            <option :value="null" disabled>{{ $t("common.choose") }}</option>
            <option v-for="type in types" :key="type.value" :value="type.value">
              {{ type.kind === "REPORT" ? $t("legal-document-wizard.report-type") : $options.filters.translate(type.name) }}
            </option>
          </select>

          <!-- TKP-235 m.196: KVKK/GDPR'de raporlar burada (sağda liste); diğer ürünlerde Raporlar ekranına yönlendirme -->
          <div v-if="isReport && !reportsInline" class="alert alert-info" data-test="report-hint">
            {{ $t("legal-document-wizard.report-hint") }}
            <b-button size="sm" variant="primary" class="mt-2 d-block" data-test="go-reports" @click="goToReports">{{ $t("legal-document-wizard.go-reports") }}</b-button>
          </div>

          <p v-else-if="isReport" class="text-muted" data-test="report-inline-hint">{{ $t("legal-document-wizard.report-inline-hint") }}</p>
          <template v-else>
            <!-- 2. adım: şablon -->
            <h5 v-if="stepVisible('template')">{{ $t("legal-document-wizard.step.template") }}</h5>
            <select v-if="stepVisible('template')" class="form-control mb-3" v-model="state.template" data-test="template" @change="templateChanged">
              <option :value="null" disabled>{{ $t("common.choose") }}</option>
              <option v-for="template in templates" :key="template._id" :value="template">{{ template.name | translate }}</option>
            </select>

            <!-- 3. adım: filtreler (md.3) -->
            <template v-if="stepVisible('filters')">
              <h5>{{ $t("legal-document-wizard.step.filters") }}</h5>
              <!-- TKP-174 md.39 zorunlu etiketi; md.40 filtreler bağımsız (üstten alta zorunluluk yok); md.41 birim seçiliyse süreç araması daralır -->
              <!-- TKP-197: 16 filtre — ilk 5 sabit, diğerleri açılır panelde; seçenekler envanterde geçen tekil değerler, diğer seçimlere göre daralır -->
              <div v-for="field in pinnedFields" :key="field" class="mb-3" :data-test="`filter-${field}`">
                <h6 class="mb-1 text-muted">
                  {{ $t(`legal-document-wizard.filters.${filterLabel[field]}`) }}
                  <span v-if="isRequired(field)" class="badge badge-soft-danger ml-1" data-test="required-badge">{{ $t("legal-document-wizard.required") }}</span>
                </h6>
                <filter-select :ref="`${field}Filter`" :multiple="true" :selected="state.criteria[field]" :placeholder="$t('common.choose')" @filter="(v, cb) => search(field, v, cb)" @changed="(v) => select(field, v)" />
              </div>
              <div class="mb-3 border rounded">
                <div class="p-2 hand d-flex justify-content-between align-items-center" data-test="filters-more-toggle" @click="moreOpen = !moreOpen">
                  <span><i class="bx" :class="moreOpen ? 'bx-chevron-down' : 'bx-chevron-right'"></i> {{ $t("legal-document-wizard.filters.more") }}</span>
                  <b-badge v-if="moreCount" variant="primary" data-test="filters-more-count">{{ $t("legal-document-wizard.filters.more-count", { count: moreCount }) }}</b-badge>
                </div>
                <div v-show="moreOpen" class="px-2 pb-1" data-test="filters-more">
                  <div v-for="field in moreFields" :key="field" class="mb-3" :data-test="`filter-${field}`">
                    <h6 class="mb-1 text-muted">
                      {{ $t(`legal-document-wizard.filters.${filterLabel[field]}`) }}
                      <span v-if="isRequired(field)" class="badge badge-soft-danger ml-1" data-test="required-badge">{{ $t("legal-document-wizard.required") }}</span>
                    </h6>
                    <filter-select :ref="`${field}Filter`" :multiple="true" :selected="state.criteria[field]" :placeholder="$t('common.choose')" @filter="(v, cb) => search(field, v, cb)" @changed="(v) => select(field, v)" />
                  </div>
                </div>
              </div>
              <!-- TKP-174 md.38: çoklu seçimde tek metin / her biri için ayrı -->
              <div v-if="multiSelection" class="mb-3 p-2 border rounded" data-test="render-mode">
                <h6 class="mb-1 text-muted">{{ $t("legal-document-wizard.render-mode.title") }}</h6>
                <!-- TKP-197: düz satırlar (radyo grubu dar sütunda etiketleri kelime kelime kırıp başlığın üstüne bindiriyordu) -->
                <div data-test="render-mode-options">
                  <label v-for="o in renderModeOptions" :key="String(o.value)" class="d-flex align-items-start mb-1 hand render-mode-option">
                    <input type="radio" class="mt-1 mr-2 flex-shrink-0" :value="o.value" v-model="state.renderSingleDocument" :data-test="`mode-${o.value}`" @change="previewDebounced" />
                    <span>{{ o.text }}</span>
                  </label>
                </div>
              </div>
              <div v-if="missingRequired.length" class="alert alert-warning p-2 small" data-test="required-missing">{{ $t("legal-document-wizard.required-missing", { fields: missingRequiredLabels }) }}</div>
            </template>
          </template>

          <div v-if="!isReport" class="mt-4 d-flex justify-content-between align-items-center">
            <small class="text-muted" data-test="summary">{{ summaryText }}</small>
            <b-button variant="success" :disabled="!completable || isUpdating" data-test="complete" @click="complete">{{ $t("legal-document-wizard.complete") }}</b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- TKP-235 m.196: Rapor seçiliyken önizleme yerine rapor listesi -->
    <div v-if="isReport && reportsInline" class="col-lg-7" data-test="wizard-reports">
      <div class="card">
        <div class="card-body">
          <h5 class="mb-3">{{ $t("report.plural") }}</h5>
          <report-list :product="product" />
        </div>
      </div>
    </div>
    <!-- canlı önizleme (md.4–5): "#" alanları envanterden süzülmüş hâliyle -->
    <div v-else class="col-lg-7">
      <div class="card">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="mb-0">{{ $t("legal-document-wizard.preview") }}</h5>
            <b-badge v-if="preview && preview.hasData" variant="light" data-test="preview-name">{{ preview.name }}</b-badge>
          </div>
          <content-loading v-if="isPreviewing" />
          <div v-else-if="!preview" class="text-muted" data-test="preview-empty">{{ $t("legal-document-wizard.preview-empty") }}</div>
          <div v-else-if="!preview.hasData" class="alert alert-warning" data-test="preview-no-data">{{ $t("legal-document-wizard.preview-no-data") }}</div>
          <div v-else class="legal-document-preview" data-test="preview-content" v-html="previewHtml"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { canComplete, categoryIdsOf, documentTypes, emptyCriteria, FILTER_LABEL, hasMultiSelection, isReportType, missingRequired, MORE_CRITERIA, moreSelectedCount, PINNED_CRITERIA, previewSummary, reachableStep, requiredFields, searchOptions, toCriteriaPayload } from "./wizard";
import ReportList from "../report-template/report-list"; // TKP-235 m.196

const state = "organizationLegalDocumentTemplate";

export default {
  name: "legal-document-wizard",
  components: { ReportList },
  props: ["product"],
  data() {
    return {
      state: { type: null, template: null, renderSingleDocument: false, criteria: emptyCriteria() },
      categories: [],
      allTemplates: null, // TKP-174 md.37: ürünün tüm şablonları — tip listesi bunlardan türer
      templates: [],
      preview: null,
      isPreviewing: false,
      pinnedFields: PINNED_CRITERIA,
      moreFields: MORE_CRITERIA,
      filterLabel: FILTER_LABEL,
      moreOpen: false,
      options: {}, // TKP-197: filtre → seçenekler (envanterden, diğer seçimlere göre daralmış)
      optionsTimer: null,
      timer: null,
    };
  },
  computed: {
    types() {
      return documentTypes(this.categories, this.currentAccountCan("LEGAL_DOCUMENT_WIZARD__CAN_SEE_REPORT_TYPE"), this.allTemplates); // karar 2; md.37 tekil + boşsuz
    },
    moreCount() {
      return moreSelectedCount(this.state.criteria);
    },
    multiSelection() {
      return hasMultiSelection(this.state.criteria);
    },
    renderModeOptions() {
      return [
        { value: false, text: this.$t("legal-document-wizard.render-mode.separate") },
        { value: true, text: this.$t("legal-document-wizard.render-mode.single") },
      ];
    },
    missingRequired() {
      return missingRequired(this.state.template, this.state.criteria);
    },
    missingRequiredLabels() {
      return this.missingRequired.map((f) => this.$t(`legal-document-wizard.filters.${this.filterLabel[f]}`)).join(", ");
    },
    /** TKP-235 m.196: KVKK ve GDPR'de Raporlar menüden kalktı — liste sihirbazın içinde. */
    reportsInline() {
      return ["KVKK", "GDPR"].includes(String(this.product).toUpperCase());
    },
    isReport() {
      return isReportType(this.state.type);
    },
    completable() {
      return canComplete(this.state, this.preview);
    },
    isUpdating() {
      return this.$store.getters[`${state}/isUpdating`];
    },
    previewHtml() {
      return (this.preview?.content || []).join("");
    },
    summaryText() {
      const summary = previewSummary(this.preview);
      if (this.missingRequired.length) return this.$t("legal-document-wizard.required-missing", { fields: this.missingRequiredLabels });
      if (!this.preview) return "";
      if (!summary.hasData) return this.$t("legal-document-wizard.preview-no-data");
      return this.$t("legal-document-wizard.summary", { count: summary.count });
    },
  },
  async mounted() {
    await this.loadCategories();
  },
  methods: {
    stepVisible(step) {
      return reachableStep(this.state, step);
    },
    async loadCategories() {
      const response = await this.$store.dispatch("organizationLegalDocumentTemplateCategory/paginate", { options: { limit: 200, sort: { name: "asc" } }, query: {} });
      this.categories = response.data?.docs ?? [];
      // md.37: ürünün şablonları (tip listesi şablonu olan kategorilerden türer)
      const all = await this.$store.dispatch(`${state}/paginate`, { options: { limit: 500, sort: { name: "asc" } }, query: { products: { value: this.product, isObject: true } } });
      this.allTemplates = all.data?.docs ?? [];
    },
    isRequired(field) {
      return requiredFields(this.state.template).includes(field);
    },
    async typeChanged() {
      this.state.template = null;
      this.preview = null;
      this.templates = [];
      if (this.isReport || !this.state.type) return;
      const ids = categoryIdsOf(this.state.type);
      const response = await this.$store.dispatch(`${state}/paginate`, {
        options: { limit: 200, sort: { name: "asc" } },
        query: { products: { value: this.product, isObject: true }, category: { value: ids.length > 1 ? { $in: ids } : ids[0], isObject: true } },
      });
      this.templates = response.data?.docs ?? [];
    },
    templateChanged() {
      // md.38: seçenek şablonun ayarıyla başlar
      this.state.renderSingleDocument = !!(this.state.template && this.state.template.renderSingleDocument);
      this.loadOptions();
      this.previewDebounced();
    },
    /** TKP-197: seçenekler envanterden — her filtre için diğer seçimlere uyan tekil değerler. */
    async loadOptions() {
      try {
        const { data } = await this.$store.dispatch(`${state}/filterOptions`, { criteria: toCriteriaPayload(this.state.criteria) });
        this.options = data || {};
        // seçim kutuları listelerini yalnız açılışta / yazarken ister — daralan seçenekleri hemen yansıtsınlar
        for (const field of [...this.pinnedFields, ...this.moreFields]) {
          const ref = this.$refs[`${field}Filter`];
          const box = Array.isArray(ref) ? ref[0] : ref;
          // kutuda yazılmış arama varsa onunla süz (yoksa yükleme bitince liste aramayı ezer ve hepsini gösterir)
          const typed = box && box.$refs && box.id && box.$refs[box.id] ? box.$refs[box.id].search : "";
          if (box && typeof box.searchChanged === "function") box.searchChanged(typed || "*");
        }
      } catch (error) {
        this.options = {};
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    optionsDebounced() {
      if (this.optionsTimer) clearTimeout(this.optionsTimer);
      this.optionsTimer = setTimeout(() => this.loadOptions(), 300);
    },
    goToReports() {
      this.$router.push(`/${String(this.product).toLowerCase()}/report`);
    },
    async search(field, value, callback) {
      // TKP-197: seçenekler envanterden (tekil, diğer seçimlere göre daralmış); arama istemcide ada göre
      if (!this.options || !this.options[field]) await this.loadOptions();
      callback(searchOptions((this.options || {})[field] || [], value, (n) => this.$options.filters.translate(n) || ""));
    },
    select(field, value) {
      this.$set(this.state.criteria, field, (value || []).map((v) => ({ _id: v._id, name: v.name })));
      this.optionsDebounced(); // diğer filtrelerin seçenekleri bu seçime göre daralır
      this.previewDebounced();
    },
    previewDebounced() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refreshPreview(), 400); // md.4: seçim yapıldıkça, ama her tuşta değil
    },
    async refreshPreview() {
      if (!this.state.template || !this.state.template._id) {
        this.preview = null;
        return;
      }
      this.isPreviewing = true;
      try {
        const { data } = await this.$store.dispatch(`${state}/preview`, { template: this.state.template._id, criteria: toCriteriaPayload(this.state.criteria), renderSingleDocument: this.state.renderSingleDocument });
        this.preview = data;
      } catch (error) {
        this.preview = null;
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.isPreviewing = false;
      }
    },
    async complete() {
      try {
        const { data } = await this.$store.dispatch(`${state}/generate`, { template: this.state.template._id, criteria: toCriteriaPayload(this.state.criteria), renderSingleDocument: this.state.renderSingleDocument });
        // TKP-92: yayımlandı mı, onaya mı düştü? Sunucu karar verir (kapsamlı kullanıcının metni onaya gider)
        this.$toast.success(this.$t(data.approved ? "legal-document-wizard.completed" : "legal-document-wizard.sent-for-approval", { count: data.created }));
        this.$store.commit("organizationLegalDocument/tab", data.approved ? "document" : "review");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>

<style scoped>
.legal-document-preview {
  max-height: 70vh;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 1rem;
}
</style>
